import Vue from 'vue';
import VueMeta from 'vue-meta';
import VueVirtualScroller from 'vue-virtual-scroller';

import 'lazysizes';

import { Context } from '@/@types/ssr';

import { getApolloProvider } from '@/apollo';
import { createI18n } from '@/i18n';
import { createRouter } from '@/routing';
import createStore from '@/store';

import SSRApp from '@/SSRApp.vue';
import InjectConstants from '@/constants/InjectConstants.vue';

import {
	initApp,
	initClientStorage,
	initRouter,
	initLanguage,
	initLocale,
	initFirebase,
	initStore,
	initSSR,
	jwIdServerClientHandover,
	refreshToken,
} from '@/main.common';

import { createThumborFilter } from '@/filters/thumbor';

import { addServerCustomHeaders, getAxios, getAxiosCached, setAxiosInterceptors } from '@/helpers/axios-helper';
import { initSentry, Sentry } from '@/helpers/sentry-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { setVm } from '@/helpers/vm-helper';

import { GlobalDirectivesPlugin } from '@/plugins/global-directives-webapp';
import { GlobalComponentsPlugin } from '@/plugins/global-components';
import Jw, { Options as JwOptions } from '@/plugins/jw';
import Ready, { Options as ReadyOptions } from '@/plugins/ready';

import { ReadyType } from '@/stores/ready.store';

import '@/styles/global.scss';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import '@fortawesome/fontawesome-svg-core/styles.css';

Vue.config.ignoredElements = [/^ion-/];
Vue.config.productionTip = process.env.NODE_ENV !== 'production';
Vue.config.performance = process.env.NODE_ENV === 'production';

Vue.use(VueMeta, {});

global.IS_TOUCH = process.client ? !window.matchMedia('(hover: hover)').matches : false;

Vue.use(GlobalComponentsPlugin);
Vue.use(GlobalDirectivesPlugin);

// START: CLS Check
if (process.env.NODE_ENV !== 'production') {
	import('@/plugins/cls-check').then(module => Vue.use(module.ClsCheckPlugin));
}
// END: CLS Check

export default async (context: Context) => {
	// pass context down to App instance and all Components
	Vue.prototype.$context = context;

	initFirebase();

	/***** SERVER ENV *****/
	if (process.server) {
		const customHeaders = context.ssr.data?.SERVER_ENV?.customHeaders;
		if (customHeaders) {
			addServerCustomHeaders(customHeaders);
		}
		// clear SERVER_ENV to avoid leaks
		delete context.ssr.data.SERVER_ENV;
	}
	/***** SERVER ENV *****/

	let localStoragePromise;
	let ionicPromise;
	if (process.client) {
		// Start loading Ionic
		ionicPromise = import('@/ionic.loader');

		localStoragePromise = initClientStorage();
	}

	if (PLATFORM.IS_SSR) {
		// Make basic SSR init before init Store
		initSSR(context);
	}

	await localStoragePromise;
	const store = await createStore(context);
	context.store = store;

	let refreshTokenPromise;
	if (process.client) {
		refreshTokenPromise = refreshToken(context);
	}

	/***** READY STORE *****/
	Vue.use<JwOptions>(Jw);
	Vue.use<ReadyOptions>(Ready, { store });
	/***** READY STORE *****/

	/***** Sentry init *****/
	if (process.client) {
		/**
		 * Sentry Init Client Side
		 */

		try {
			initSentry().then(() => {
				// Sentry User ID
				Vue.$jw.ready?.waitFor(ReadyType.JW_ID).then(() => {
					Sentry.setUser({ id: store.state.user.jwId });
				});
			});
		} catch (error) {
			// TODO Track this.
		}
	}
	/***** Sentry init *****/

	const i18n = createI18n();
	context.i18n = i18n;

	context.webLocale = await initLocale(context);

	if (process.client) {
		/***** AXIOS INIT *****/
		// axios for personalized urls
		setAxiosInterceptors(getAxios(true));

		// axios for server cached urls
		setAxiosInterceptors(getAxiosCached());
		/***** AXIOS INIT *****/
	}

	const router = createRouter(context.webLocale, store);
	context.router = router;

	// This line MUST come after `await initLocale(context)`
	// It's here because `initLanguage` needs the router to check if we're on a sports route
	context.language = await initLanguage(context);

	Vue.filter('Thumbor', createThumborFilter(context.store.state));

	TrackingHelper.resetState();
	TrackingHelper.setStore(store);

	// wait for the refreshToken promise to finish Apollo init (it needs a fresh access token)
	await refreshTokenPromise;

	if (process.client) {
		await jwIdServerClientHandover(store);
	}

	if (PLATFORM.IS_SPA) {
		// If SPA we need to wait for the store to be created to make basic SPA init
		const { initSPA } = await import(/* webpackChunkName: "main-common" */ '@/main.common');
		await initSPA(
			context,
			getApolloProvider({
				deviceId: store.state.user.jwId,
				store,
			})
		);
	}

	if (process.client) {
		await ionicPromise;
		const Ionic = (await ionicPromise)?.default;
		Ionic && Vue.use(Ionic);

		// jwID fallback
		if (!store.state.user.jwId) {
			// in case the server doesn't provide a jwId
			await store.dispatch(
				'user/fetchJwId',
				getApolloProvider({
					deviceId: store.state.user.jwId,
					store,
				})
			);
		}
	}

	/***** Vue Apollo init *****/
	const appApolloProvider = getApolloProvider({
		deviceId: store.state.user.jwId,
		store,
	});
	/***** Vue Apollo init *****/

	await initStore(context);

	await initRouter(context);

	await initApp(context);

	// plugins that need to be used in client and server:
	Vue.use(VueVirtualScroller);

	context.app = new Vue({
		router,
		store,
		i18n,
		apolloProvider: appApolloProvider,
		render: h => h(InjectConstants, [h(SSRApp)]),
	});

	setVm(context.app);

	return context;
};
